import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import { Button, Form, Input, Row, Col, Upload, Icon, message } from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import { FormComponentProps } from 'antd/lib/form';
import { buttonStyle, commonStyles } from 'src/Library/CommonStyles';
import { getS3Links, uploadToS3 } from 'src/api/invoice';
import { getFileExtension } from 'src/utils/utils';
import { confirmBooking } from 'src/api/booking';
import Protip from './Protip';
import { setPrevPagination } from 'src/actions/bookingInquiryActions';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { tokens } from 'src/design-system';
import Asterisk from 'src/components/common/Asterisk';

const styles = (theme) => ({
  cancelButton: {
    ...buttonStyle('#424242', '#F2F4F6', '1px solid #F2F4F6'),
    padding: '0px 25px',
    marginRight: '16px',
    borderRadius: '3px',
  },
  okButton: {
    ...buttonStyle(tokens.colors.bgWhite, tokens.colors.primary, '1px solid #1890FF'),
    padding: '0px 25px',
    borderRadius: '3px',
  },
  justifySpaceBetween: {
    ...commonStyles(theme).justifySpaceBetween,
    margin: '0 8px 0 0',
    alignItems: 'center',
  },
  footer: {
    padding: '24px 6% 0 24px',
    borderTop: '1px solid rgb(151,151,151, 0.2)',
    marginTop: '36px',
    position: 'absolute',
    width: '100%',
    bottom: '24px',
  },
});

interface IProps extends StylesProps<ReturnType<typeof styles>>, FormComponentProps {
  handlePrevious: () => void;
  isBooking: (booking?: boolean) => void;
  reload: () => void;
  bookingId: string;
  handleClose: () => void;
}

interface StateProps {
  isLoading?: boolean;
  isUploading?: boolean;
  fileName?: string;
  fileUrl?: string;
  protip?: boolean;
}

const AddTruckingBookingDetails = (props: IProps): React.ReactElement => {

  const {
    classes,
    form,
    handlePrevious,
    isBooking,
    reload,
    bookingId,
    handleClose,
  } = props;

  const [state, setState] = useGenericState<StateProps>({});

  const {
    isLoading,
    isUploading,
    fileName,
    fileUrl,
    protip,
  } = state;

  const handleSubmit = () => {
    form.validateFieldsAndScroll(async (err, values) => {
      if (err) return;
      setState({ isLoading: true });
      const body = {
        ...values,
        ...(fileUrl && fileName && {
          documents: [{
            url: fileUrl,
            type: 'DRIVER_LICENSE',
            name: fileName,
          }],
        }),
      };
      isBooking(true);
      const response = await confirmBooking({ confirmationDetails: body, bookingId });
      if (response.isSuccess) {
        setState({ isLoading: false, protip: true });
      } else {
        message.error(response.errorMessage);
        setState({ isLoading: false });
      }
      isBooking(false);
    });
  };

  const renderFooter = () => {
    return (
      <div className={classes.justifySpaceBetween}>
        <a style={{
          color: tokens.colors.primary,
          fontSize: `${tokens.fontSize.h3}px`,
          cursor: isLoading ? 'not-allowed' : 'pointer'
        }}
          onClick={() => !isLoading && handlePrevious() }>Previous</a>
        <Button
          className={classes.okButton}
          onClick={() => handleSubmit()}
          disabled={isUploading}
          loading={isLoading}
        >Submit</Button>
      </div>
    );
  };

  const renderTruckInfo = (props) : React.ReactElement => {
    const {
      key,
      label,
    } = props;
    return (
      <Row style={{ fontSize: `${tokens.fontSize.small}px`, marginBottom: '16px' }}>
        <Col span={3} style={{ color: '#262626', fontWeight: tokens.fontWeight.semibold, marginTop: '10px' }}>{label}<Asterisk /></Col>
        <Col span={21}>
          <Form.Item style={{ marginBottom: 0, width: '20%' }}>
            {form.getFieldDecorator(`${key}`, {
              rules: [{ required: true, message: 'Required' }],
            })(
              <Input
                placeholder={label}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderDriverLicense = () : React.ReactElement => {
    return (
      <Row style={{ fontSize: `${tokens.fontSize.small}px` }}>
        <Col span={3} style={{ color: '#262626', fontWeight: tokens.fontWeight.semibold, marginTop: '10px' }}>Driver License</Col>
        <Col span={21}>
          <Upload
            showUploadList={false}
            multiple={false}
            customRequest={file => s3LinkRequest(file)}
            disabled={isUploading}>
            <Button loading={isUploading} style={{ borderRadius: `${tokens.radius.md}px` }}>
              <Icon type="upload" /> Upload
            </Button>
          </Upload>
          <div style={{ fontSize: `${tokens.fontSize.small}px`, marginTop: '8px' }}>
            {fileName && <React.Fragment>
              <span>{fileName}</span>
              <Icon type="close" style={{ color: 'red', marginLeft: '8px' }}
                onClick={() => setState({ fileName: undefined, fileUrl: undefined })} />
            </React.Fragment>}
          </div>
        </Col>
      </Row>
    );
  };

  const onOkay = () => {
    reload();
    handleClose();
    setState({
      protip: false,
    });
  }

  const renderProtip = () => {
    return protip && <Protip
      visible={protip}
      success={'Congrats'}
      message1={'You have successfully completed the booking process.'}
      message2={'You are being redirected to Bookings'}
      okay={'Okay'}
      onOkay={onOkay}
    />;
  }

  const s3LinkRequest = async ({ file }): Promise<void> => {
    setState({
      isUploading: true,
      fileName: file.name,
    });
    const extension = getFileExtension(file.name);
    const response = await getS3Links({
      extension,
      fileName: file.name,
      document_type: file.type,
      initialPath: 'Booking',
    });
    let fileUrl: string = '';
    if (response.isSuccess) {
      const upload = await uploadToS3(response.data.post, extension, file);
      if (upload.isSuccess) fileUrl = response.data.get;
    } else {
      message.error(response.errorMessage);
    }
    setState({
      fileUrl,
      isUploading: false,
    });
  };

  return (
    <div style={{ minHeight: '550px' }}>
      <div style={{ padding: '24px' }}>
        {renderTruckInfo({
          key: 'truckNumber',
          label: 'Truck Number',
        })}
        {renderTruckInfo({
          key: 'driverName',
          label: 'Driver Name',
        })}
        {renderTruckInfo({
          key: 'driverPhoneNo',
          label: 'Driver Phone No.',
        })}
        {renderDriverLicense()}
      </div>
      <div className={classes.footer}>
        {renderFooter()}
      </div>
      {protip && renderProtip()}
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return bindActionCreators({
    reload: setPrevPagination(ownProps.type),
  }, dispatch);
};

export default connect(null, mapDispatchToProps)(
  React.memo(withStyles(styles)(Form.create()(AddTruckingBookingDetails)))
);
